import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19302" },
  { urls: "stun:stun3.l.google.com:19302" },
];

export type CallState = "idle" | "calling" | "incoming" | "connected" | "ended";
export type CallType = "audio" | "video";

export interface IncomingCall {
  callerName: string;
  callType: CallType;
}

interface UseWebRTCOptions {
  conversationId: string | null;
  userId: string | null;
  userName: string;
}

export function useWebRTC({ conversationId, userId, userName }: UseWebRTCOptions) {
  const [callState, setCallState] = useState<CallState>("idle");
  const [callType, setCallType] = useState<CallType>("audio");
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  // ── Stable refs (never change identity, safe to use in effects without deps) ──
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const channelReadyRef = useRef(false);
  const channelReadyPromiseRef = useRef<Promise<void> | null>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const isCallerRef = useRef(false);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const callTypeRef = useRef<CallType>("audio");
  const callStateRef = useRef<CallState>("idle");
  const userIdRef = useRef<string | null>(userId);
  const userNameRef = useRef(userName);

  // Keep refs in sync with latest values without causing effect re-runs
  useEffect(() => { callStateRef.current = callState; }, [callState]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);
  useEffect(() => { userNameRef.current = userName; }, [userName]);

  // ── sendSignal via ref so signaling effect never needs to re-subscribe ──
  const sendSignalFn = useCallback(async (payload: Record<string, unknown>) => {
    const channel = channelRef.current;
    if (!channel) return;
    if (!channelReadyRef.current && channelReadyPromiseRef.current) {
      await channelReadyPromiseRef.current;
    }
    if (channelRef.current !== channel || !channelReadyRef.current) return;
    await channel.send({
      type: "broadcast",
      event: "signal",
      payload: { ...payload, senderId: userIdRef.current },
    });
  }, []);

  // ── cleanup via stable ref ──
  const cleanup = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    remoteStreamRef.current = null;
    peerConnectionRef.current?.close();
    peerConnectionRef.current = null;
    pendingCandidatesRef.current = [];
    isCallerRef.current = false;
    setIsMuted(false);
    setIsCameraOff(false);
    setIncomingCall(null);
  }, []);

  // ── setupPeerConnection: always called synchronously after getLocalStream ──
  const setupPeerConnection = useCallback(() => {
    // Close any existing connection cleanly
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        sendSignalFn({ type: "ice-candidate", candidate: e.candidate.toJSON() });
      }
    };

    pc.ontrack = (e) => {
      const stream = e.streams?.[0];
      if (stream) {
        remoteStreamRef.current = stream;
        setRemoteStream(stream);
        return;
      }

      // Some browsers do not populate RTCTrackEvent.streams. Build a
      // stream from the received tracks so audio is still routed correctly.
      const fallback = remoteStreamRef.current ?? new MediaStream();
      if (!fallback.getTracks().some((track) => track.id === e.track.id)) {
        fallback.addTrack(e.track);
      }
      remoteStreamRef.current = fallback;
      setRemoteStream(fallback);
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") {
        setCallState("connected");
      } else if (pc.connectionState === "failed") {
        // Only tear down on hard failure.
        // "disconnected" is transient on mobile — ICE will retry automatically.
        cleanup();
        setCallState("ended");
        setTimeout(() => setCallState("idle"), 2000);
      }
    };

    // Add local tracks so the remote side receives our audio/video
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current!);
      });
    }

    peerConnectionRef.current = pc;
    return pc;
  }, [sendSignalFn, cleanup]);

  // Keep a ref so the signaling effect can call it without being in its deps
  const setupPeerConnectionRef = useRef(setupPeerConnection);
  useEffect(() => { setupPeerConnectionRef.current = setupPeerConnection; }, [setupPeerConnection]);

  // ── Signaling channel ────────────────────────────────────────────────────
  // Deps are ONLY [conversationId, userId].
  // All callbacks are accessed via refs so the channel never re-subscribes
  // mid-call due to callback identity changes.
  useEffect(() => {
    if (!conversationId || !userId) return;

    const channel = supabase.channel(`call-${conversationId}`, {
      config: { broadcast: { self: false } },
    });

    channel.on("broadcast", { event: "signal" }, async ({ payload }) => {
      const { type, senderId, callerName, callType: ct, sdp, candidate } =
        payload as Record<string, unknown>;

      // Ignore our own signals
      if (senderId === userId) return;

      switch (type as string) {
        // ── Incoming call notification ───────────────────────────────────
        case "call-request":
          if (callStateRef.current === "idle") {
            setIncomingCall({ callerName: callerName as string, callType: ct as CallType });
            callTypeRef.current = ct as CallType;
            setCallType(ct as CallType);
            setCallState("incoming");
          }
          break;

        // ── Receiver accepted — caller now creates and sends the offer ───
        case "call-accepted":
          if (isCallerRef.current && callStateRef.current === "calling") {
            // Move to "connected" immediately so the periodic call-request
            // re-broadcast stops — preventing the receiver from seeing a
            // second "incoming" ring after the call is established.
            setCallState("connected");
            const pc = peerConnectionRef.current!;
            const offer = await pc.createOffer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: callTypeRef.current === "video",
            });
            await pc.setLocalDescription(offer);
            sendSignalFn({ type: "offer", sdp: pc.localDescription });
          }
          break;

        case "call-rejected":
          cleanup();
          setCallState("idle");
          break;

        case "call-ended":
          cleanup();
          setCallState("ended");
          setTimeout(() => setCallState("idle"), 2000);
          break;

        // ── WebRTC offer (arrives on receiver after they sent call-accepted) ──
        case "offer": {
          // Peer connection is already set up by answerCall; reuse it
          const pc = peerConnectionRef.current ?? setupPeerConnectionRef.current();
          await pc.setRemoteDescription(
            new RTCSessionDescription(sdp as RTCSessionDescriptionInit)
          );
          // Drain any ICE candidates that arrived before the remote description
          for (const c of pendingCandidatesRef.current) {
            await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
          }
          pendingCandidatesRef.current = [];
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          sendSignalFn({ type: "answer", sdp: pc.localDescription });
          break;
        }

        case "answer":
          await peerConnectionRef.current?.setRemoteDescription(
            new RTCSessionDescription(sdp as RTCSessionDescriptionInit)
          );
          break;

        case "ice-candidate": {
          const iceInit = candidate as RTCIceCandidateInit;
          if (peerConnectionRef.current?.remoteDescription) {
            await peerConnectionRef.current
              .addIceCandidate(new RTCIceCandidate(iceInit))
              .catch(() => {});
          } else {
            pendingCandidatesRef.current.push(iceInit);
          }
          break;
        }
      }
    });

    channelReadyRef.current = false;
    channelReadyPromiseRef.current = new Promise<void>((resolve) => {
      channel.subscribe((status) => {
        if (status === "SUBSCRIBED") {
          channelReadyRef.current = true;
          resolve();
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
          resolve();
        }
      });
    });
    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelReadyRef.current = false;
      channelReadyPromiseRef.current = null;
      channelRef.current = null;
    };
  }, [conversationId, userId]); // MINIMAL deps — never re-subscribes mid-call

  // ── Start call (caller side) ────────────────────────────────────────────
  const startCall = useCallback(
    async (type: CallType) => {
      try {
        setCallType(type);
        callTypeRef.current = type;
        setCallState("calling");
        isCallerRef.current = true;

        // Get media FIRST, then set up the peer connection with the tracks
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          video: type === "video"
            ? { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }
            : false,
        });
        localStreamRef.current = stream;
        setLocalStream(stream);

        setupPeerConnection(); // adds tracks from localStreamRef
         await sendSignalFn({ type: "call-request", callerName: userNameRef.current, callType: type });
      } catch (err) {
        console.error("Error starting call:", err);
        cleanup();
        setCallState("idle");
      }
    },
    [setupPeerConnection, sendSignalFn, cleanup]
  );

  // Re-broadcast call-request every 4 s so the remote side picks it up even
  // if they navigate to the chat page after the first broadcast.
  const callStateForInterval = callState; // captured for the effect dep
  useEffect(() => {
    if (callStateForInterval !== "calling") return;
    const interval = setInterval(() => {
      if (callStateRef.current === "calling") {
        void sendSignalFn({
          type: "call-request",
          callerName: userNameRef.current,
          callType: callTypeRef.current,
        });
      }
    }, 4000);
    // Auto-cancel after 60 s if nobody answers
    const timeout = setTimeout(() => {
      if (callStateRef.current === "calling") {
        cleanup();
        setCallState("idle");
      }
    }, 60000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [callStateForInterval, sendSignalFn, cleanup]);

  // ── Answer call (receiver side) ─────────────────────────────────────────
  const answerCall = useCallback(async () => {
    try {
      // Complete ALL setup before signalling the caller.
      // If we send call-accepted before the PC is ready the offer may arrive
      // before we have local tracks — resulting in a one-way or silent call.
      const type = callTypeRef.current;
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: type === "video"
          ? { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } }
          : false,
      });
      localStreamRef.current = stream;
      setLocalStream(stream);

      setupPeerConnection(); // adds tracks — must happen before call-accepted
      setIncomingCall(null);
      setCallState("connected");
      await sendSignalFn({ type: "call-accepted" });
    } catch (err) {
      console.error("Error answering call:", err);
      setCallState("idle");
    }
  }, [setupPeerConnection, sendSignalFn]);

  // ── Allow PatientCallListener to trigger incoming state externally ──────
  const triggerIncomingCall = useCallback((callerName: string, type: CallType) => {
    setIncomingCall({ callerName, callType: type });
    callTypeRef.current = type;
    setCallType(type);
    setCallState("incoming");
  }, []);

  const rejectCall = useCallback(() => {
    sendSignalFn({ type: "call-rejected" });
    cleanup();
    setCallState("idle");
  }, [sendSignalFn, cleanup]);

  const endCall = useCallback(() => {
    sendSignalFn({ type: "call-ended" });
    cleanup();
    setCallState("ended");
    setTimeout(() => setCallState("idle"), 2000);
  }, [sendSignalFn, cleanup]);

  const toggleMute = useCallback(() => {
    localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !t.enabled; });
    setIsMuted((prev) => !prev);
  }, []);

  const toggleCamera = useCallback(() => {
    localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = !t.enabled; });
    setIsCameraOff((prev) => !prev);
  }, []);

  const toggleSpeaker = useCallback(() => {
    setIsSpeakerOn((prev) => !prev);
  }, []);

  return {
    callState,
    callType,
    isMuted,
    isCameraOff,
    isSpeakerOn,
    incomingCall,
    localStream,
    remoteStream,
    startCall,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleCamera,
    toggleSpeaker,
    triggerIncomingCall,
  };
}
